import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from 'react-phone-number-input';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PhoneNumberField from '../../../components/InputFields/PhoneNumberField';
import Note from '../../../components/Notes/Note';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-ico.svg';
import themeColors from '../../../assets/styles/themeColors';
import { pxToRem } from '../../../assets/styles/helper';
import SectionLoader from '../../../components/Loader/SectionLoader';
import ModalBody from '../../../components/Modal/ModalBody';
import ModalButtons from '../../../components/Modal/ModalButtons';
import { postPhoneNumber } from '../../../store/actions/user/userActions';
import { enteredPhoneNumberValidatorAndFormatter } from '../../../util/helpers/phoneNumberHelpers';
import {
  selectPortalGatewayISOCodesListToIgnoreAreaCode,
  selectSettings,
} from '../../../store/selectors/settingsSelectors';
import { selectAuthUser } from '../../../store/selectors/userSelectors';
import { getCurrentAccount } from '../../../store/selectors/accountSelectors';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import {
  accountTypeKeys,
  accountTypes,
} from '../../../util/enum/api/accountTypes';
import { twoFaPhoneNumberValidationSchema } from '../../../validation/twoFaPhoneValidationSchema';

const AddPhoneDetailsNote = styled.div`
  span {
    font-size: 14px;
  }
`;

const ModalActionButtons = styled(ModalButtons)`
  button {
    font-size: 16px;
  }
`;

const PriceAlertNote = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  margin-top: ${pxToRem(16)};
  padding: ${pxToRem(10)} ${pxToRem(12)};
  border-radius: ${pxToRem(8)};
  background-color: ${themeColors.colorBackgroundSecondary};
  border: 1px solid ${themeColors.colorCardBorder};
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
  line-height: 140%;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    fill: ${themeColors.colorTextSecondary};
  }
`;

const TwoFaPhoneAddPhone = ({
  onButtonClick,
  handleNext,
  setError,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const user = useSelector(selectAuthUser);
  const { UserUid } = user;
  const settings = useSelector(selectSettings);
  const isPriceAlertActive = settings?.IsPriceAlertActive;
  const account = useSelector(getCurrentAccount);
  // QA: the price-alert number note must not show for IRA accounts.
  const isIRAAccount =
    parseEnumType(accountTypeKeys, account?.AccountType) === accountTypes.IRA;

  const handleSubmitPhoneNumber = (values, { setFieldError }) => {
    const { PhoneNumber } = values;

    const isOnlyNumbers =
      PhoneNumber.replace('+', '').match(/^[0-9]+$/) != null;

    if (!isOnlyNumbers && PhoneNumber.length < 15) {
      setFieldError(
        'PhoneNumber',
        t('validationSchema.general.phoneNumberOnlyNumbers'),
      );
    } else {
      const phoneNumberJsonParsed = JSON.parse(
        PhoneNumber.replace('+', '').replace('-', ''),
      );

      if (!isPossiblePhoneNumber(`+${phoneNumberJsonParsed}`)) {
        setFieldError(
          'PhoneNumber',
          t('validationSchema.general.phoneNumberNotValidLength'),
        );
        return;
      }

      if (!isValidPhoneNumber(`+${phoneNumberJsonParsed}`)) {
        setFieldError(
          'PhoneNumber',
          t('validationSchema.general.phoneNumberNotValidFormat'),
        );
        return;
      }

      const phoneNumber = enteredPhoneNumberValidatorAndFormatter(
        phoneNumberJsonParsed,
        setFieldError,
        selectedISOCodesListToIgnoreAreaCode,
      );

      if (!phoneNumber) {
        setFieldError(
          'PhoneNumber',
          t('validationSchema.general.phoneNumberNotValidFormat'),
        );
        return;
      }

      dispatch(
        postPhoneNumber({
          userUid: UserUid,
          data: {
            ...phoneNumber,
          },
          handleNext,
          onButtonClick,
          setIsLoading,
          setError,
          onSuccess,
          onError,
        }),
      );
    }
  };

  return (
    <SectionLoader isLoading={isLoading}>
      <ModalBody>
        <Formik
          onSubmit={handleSubmitPhoneNumber}
          validationSchema={twoFaPhoneNumberValidationSchema}
          initialValues={{
            Iso3CountryCode: '',
            AreaCode: '',
            LocalNumber: '',
          }}
          enableReinitialize
        >
          {({ isValid, setFieldValue }) => (
            <Form>
              <PhoneNumberField
                name="PhoneNumber"
                onPhoneChange={(phoneNum) =>
                  setFieldValue('PhoneNumber', phoneNum)
                }
              />
              {isPriceAlertActive && !isIRAAccount && (
                <PriceAlertNote>
                  <InfoIcon />
                  {t('twoFa.loginSetup.phone.phoneList.priceAlertNumberNote')}
                </PriceAlertNote>
              )}
              <AddPhoneDetailsNote>
                <Note
                  marginTop={24}
                  textAlignCenter
                  bold
                  text={t(
                    'twoFa.loginSetup.phone.phoneList.addNewPhoneDetails',
                  )}
                />
              </AddPhoneDetailsNote>

              <ModalActionButtons
                isHorizontal
                marginTop
                primaryButtonProps={{
                  type: 'submit',
                  label: t('common.submit'),
                  disabled: !isValid,
                  fontSize: '16px',
                }}
                secondaryButtonProps={{
                  onClick: onButtonClick,
                  label: t('common.cancel'),
                  fontSize: '16px',
                }}
              />
            </Form>
          )}
        </Formik>
      </ModalBody>
    </SectionLoader>
  );
};

TwoFaPhoneAddPhone.propTypes = {
  onButtonClick: PropTypes.func,
  handleNext: PropTypes.func,
  setError: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  errors: PropTypes.shape({
    PhoneNumber: PropTypes.string,
  }),
};

export default TwoFaPhoneAddPhone;
